import { useEffect, useRef, useState } from 'react';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';

export interface ReadMoreTextProps {
    /** Plain text content (use `html` for rich content instead). */
    text?: string;
    /** Rich-text (HTML) content — rendered in place of `text` when set. */
    html?: string;
    /** Lines shown when collapsed (default 3). */
    lines?: number;
    /** Class for the text itself. */
    className?: string;
    /** Class for the read-more/less toggle. */
    toggleClassName?: string;
    /**
     * When set, Read more is a link instead of an in-place expand — the text
     * stays clamped and the full copy lives at the target. An in-page anchor
     * (`#id`) smooth-scrolls to it (e.g. the product-details block); any other
     * href is a client-side route link (e.g. the professional's profile).
     */
    anchorHref?: string;
}

// Shared Read-more affordance styling (link + button variants).
const READ_MORE_LINK_CLASS =
    'inline-block font-sans underline max-md:mt-2 max-md:-mb-2 max-md:py-2 max-md:text-sm md:mt-[0.72rem] md:text-[1.248rem]';

/**
 * Clamps the content to `lines` and reveals a Read more / Read less toggle only
 * when it actually overflows (re-measures on font load + resize). With
 * `anchorHref` the toggle is a link to the full copy elsewhere on the page.
 */
export const ReadMoreText = ({
    text,
    html,
    lines = 3,
    className,
    toggleClassName,
    anchorHref,
}: ReadMoreTextProps) => {
    const ref = useRef<HTMLElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const measure = () => {
            const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 1;
            setOverflows(
                Math.round(el.scrollHeight / lineHeight) > lines + 0.5,
            );
        };
        measure();
        document.fonts?.ready.then(measure).catch(() => {});
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [text, html, lines]);

    const clampStyle =
        expanded && !anchorHref
            ? undefined
            : ({
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: lines,
                  overflow: 'hidden',
              } as const);

    // Smooth-scroll to the anchor target (instant under reduced motion). The
    // href stays real so no-JS agents and crawlers still get a working link;
    // when the target isn't mounted the native jump is a harmless no-op.
    const handleAnchorClick = (event: React.MouseEvent) => {
        if (!anchorHref?.startsWith('#')) return;
        const target = document.getElementById(anchorHref.slice(1));
        if (!target) return;
        event.preventDefault();
        const reduceMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        target.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start',
        });
    };

    return (
        <div>
            {html ? (
                <div
                    ref={ref as React.RefObject<HTMLDivElement>}
                    className={cn('m-0', className)}
                    style={clampStyle}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            ) : (
                <p
                    ref={ref as React.RefObject<HTMLParagraphElement>}
                    className={cn('m-0', className)}
                    style={clampStyle}
                >
                    {text}
                </p>
            )}
            {overflows && anchorHref ? (
                anchorHref.startsWith('#') ? (
                    <a
                        href={anchorHref}
                        onClick={handleAnchorClick}
                        className={cn(READ_MORE_LINK_CLASS, toggleClassName)}
                    >
                        Read more
                    </a>
                ) : (
                    <RoccoLink
                        to={anchorHref}
                        className={cn(READ_MORE_LINK_CLASS, toggleClassName)}
                    >
                        Read more
                    </RoccoLink>
                )
            ) : overflows ? (
                <button
                    type="button"
                    onClick={() => setExpanded((value) => !value)}
                    className={cn(
                        // Expand the mobile tap target (padding + matching
                        // negative bottom margin keeps the visual layout put).
                        'font-sans underline max-md:mt-2 max-md:-mb-2 max-md:py-2 max-md:text-sm md:mt-[0.72rem] md:text-[1.248rem]',
                        toggleClassName,
                    )}
                >
                    {expanded ? 'Read less' : 'Read more'}
                </button>
            ) : null}
        </div>
    );
};
